import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { format, addDays, compareAsc } from "date-fns";
import ExcelJS from "exceljs";
import { saveAs } from 'file-saver';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    CircularProgress,  
    Tooltip, 
    IconButton,  
} from '@mui/material';
import {
    Visibility,
    PersonPin,
    Download,
    // CleaningServices,
    // Update,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { capitalizeFirstLetter, formatNumber } from 'state/helpers';
import { logClientAction } from 'state/logger';
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import ConfirmDialog from 'components/ConfirmDialog';
import DataGridComponent from 'components/DataGridComponent';
import CardList from 'components/CardList';
import InvoiceDialog from 'scenes/inflightSales/InvoiceDialog';
import CrewListDialog from 'scenes/inflightSales/CrewListDialog';
import InvoiceFilterDialog from 'scenes/inflightSales/InvoiceFilterDialog';


const Invoices = () => {
    useAuthCheck();
    const user = useSelector((state) => state.user); 
    const timeMode = useSelector((state) => state.timemode);  
    const crewRanks = useSelector((state) => state.crewRanks);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);
    const canEditInvoice = hasRight(permissionMap, "Invoices", "update", false);
    const canEditCrewList = hasRight(permissionMap, "Invoices", "editcrewlist", false);
    const canExport = hasRight(permissionMap, "Invoices", "export", false);

    const { startDate, endDate } = useDateRangeContext();
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);    

    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState({ isInventoryUpdated: 'ALL', hasDiscrepancy: 'ALL' });
    const defaultFilters = {
        isInventoryUpdated: 'ALL',
        hasDiscrepancy: 'ALL',
    };

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
    const [openCrewListDialog, setOpenCrewListDialog] = useState(false);

    const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
    const [selectedCrewCode, setSelectedCrewCode] = useState('');
    const [selectedCrewName, setSelectedCrewName] = useState('');
    const [selectedFlights, setSelectedFlights] = useState([]);
    const [crewMembers, setCrewMembers] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);

    // const [invoicedFlights, setInvoicedFlights] = useState([]);
    const [invoices, setInvoices] = useState([]);
    

    const handleInvoices = useCallback(async () => {
        if (!startDate || !endDate) return [];

        try {           
            setError(null); 
            setLoading(true);
                        
            const url = '/inflightsales/invoices';
            const payload = { 
                startDate: addDays(startDate, -1), 
                endDate: addDays(endDate, 1),
            };
            
            const res = await api.post(url, payload);
            if (res.data) {        
                const filteredInvoices = res.data.filter(invoice => {
                    const flightDateToCompare = 
                        timeMode === 'UTC' 
                            ? invoice.flightDate 
                            : invoice.flightDateLocalBase;
                    
                    return (
                        compareAsc(flightDateToCompare, startDate) >= 0 &&
                        compareAsc(flightDateToCompare, endDate) <= 0
                    );                    
                })
                
                setInvoices(filteredInvoices);
            };
        } catch (error) {
            setError(error);
        } finally {
            setLoading(false);
        }
    }, [timeMode, startDate, endDate]); 

    useEffect(() => {
        handleInvoices();      
    }, [handleInvoices]);
    
    const filteredInvoices = useMemo(() => {
        if (!Array.isArray(invoices)) {
            return []; 
        }
    
        if (!filters) return invoices; 
    
        return invoices.filter((invoice) => {
            return Object.entries(filters).every(([key, value]) => {
                if (!value) return true; // If no value, don't filter on this key
                
                if (key === 'flightDate') {
                    const formattedRouteDate = invoice[key] ? format(new Date(invoice[key]), 'yyyy-MM-dd') : null;
                    const formattedFilterDate = value ? format(new Date(value), 'yyyy-MM-dd') : null;
                    return formattedRouteDate === formattedFilterDate;
                }
                
                if (key === 'crewRoute'  || key === 'flightNo') {
                    return invoice[key]?.toLowerCase().includes(value.toLowerCase());
                }

                if (key === 'isInventoryUpdated') {
                    if (value === 'ALL') return true;
                    if (value === 'UPDATED') return invoice[key] === true;
                    if (value === 'NOT_UPDATED') return invoice[key] === false;
                }

                if (key === 'hasDiscrepancy') {
                    if (value === 'ALL') return true;
                    const products = invoice.products || [];
                    const inventoryProducts = invoice.inventoryProducts || [];
                    const names = new Set([
                        ...products.map(p => p.productName),
                        ...inventoryProducts.map(p => p.productName),
                    ]);
                    const discrepant = [...names].some(name => {
                        const sell = products.find(p => p.productName === name);
                        const consume = inventoryProducts.find(p => p.productName === name);
                        return (sell?.preorderQuantity || 0) !== (consume?.preorderConsume || 0)
                            || (sell?.directQuantity   || 0) !== (consume?.directConsume   || 0);
                    });
                    if (value === 'HAS_DISCREPANCY') return discrepant;
                    if (value === 'NO_DISCREPANCY')  return !discrepant;
                }

                // For other fields, continue normal comparison
                return invoice[key] === value;
            });
        });
    }, [invoices, filters]);

    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };

    const handleCloseCrewListDialog = () => {    
        setCrewMembers([]);      
        setOpenCrewListDialog(false); 
    };
    
    const handleCloseInvoiceDialog = () => {
        setSelectedInvoiceId(null);
        setSelectedCrewCode(null);
        setSelectedCrewName(null);
        setSelectedFlights([]);  
        setCrewMembers([]);
        setSelectedProducts([]);
        setOpenInvoiceDialog(false); 
    };

    const handleViewClick = async () => {        
        setOpenInvoiceDialog(true);
    };

    const handleInvoiceSaved = (flights) => {
        // setInvoicedFlights(prev => [...prev, ...flights]);
        handleInvoices();
    };
        
    const handleEditClick = async () => {
        setOpenCrewListDialog(true);
    };
    
    const handleCrewListSaved = (crewMembers) => {
        setCrewMembers(prev => [...prev, ...crewMembers]);
        handleInvoices();
    };
    
    const handleExcelJSDownload = () => {
        const exportToExcel = async (data, fileName = "Invoices.xlsx") => {
            try {
                const workbook = new ExcelJS.Workbook();
                const worksheet = workbook.addWorksheet("Invoices");
        
                // 🔹 Extract Unique Categories and Fields from categorySummary
                const categoryInfo = data.reduce(
                    (acc, invoice) => {
                        const categorySummary = invoice?.categorySummary || {};
                        Object.entries(categorySummary).forEach(([category, categoryData]) => {
                            acc.categories.add(category);
                            Object.keys(categoryData).forEach((field) => acc.fields.add(field));
                        });
                        return acc;
                    },
                    { categories: new Set(), fields: new Set() }
                );
        
                const categories = Array.from(categoryInfo.categories)?.filter(category => category !== 'Equipment');
                const allFields = Array.from(categoryInfo.fields);
        
                // 🔹 Define Fixed & Dynamic Columns
                const fixedColumns = [
                    "No", 
                    "InputId",
                    "InputBy",
                    "Flight Date", 
                    "Flight No", 
                    "Crew Route", 
                    "No Of Flights",
                    "SectorType",
                    "Crew Code", 
                    "Crew Name", 
                    "Post", 
                    "Crew Count",
                    "Total Amount", 
                    "Preorder Commission", 
                    "Direct Commission", 
                    "Payable Amount",
                    "Preorder Commission (1 Crew)", 
                    "Direct Commission (1 Crew)"
                ];
        
                const categoryColumns = categories.flatMap(category =>
                    allFields.map(field => `${category} ${field}`)
                );
        
                const allColumns = [...fixedColumns, ...categoryColumns];
        
                // 🔹 Calculate Last Column Letter for Dynamic Merging
                const getExcelColumnLetter = (colIndex) => {
                    let letter = "";
                    while (colIndex >= 0) {
                        letter = String.fromCharCode((colIndex % 26) + 65) + letter;
                        colIndex = Math.floor(colIndex / 26) - 1;
                    }
                    return letter;
                };
        
                const totalColumns = allColumns.length;
                const lastColumnLetter = getExcelColumnLetter(totalColumns - 1);
        
                // 🔹 Insert Metadata Rows First
                worksheet.addRow(["Invoices"]);
                worksheet.mergeCells(`A1:${lastColumnLetter}1`);
                worksheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
                worksheet.getCell("A1").font = { size: 16, bold: true };
        
                const fullName = user?.firstName && user?.lastName ? `${user.lastName} ${user.firstName}` : "Unknown";
                const createdDate = `Reported by: ${fullName}, at: ${new Date().toLocaleString()}`;
                worksheet.addRow([createdDate]);
                worksheet.mergeCells(`A2:${lastColumnLetter}2`);
                worksheet.getCell("A2").alignment = { horizontal: "center", vertical: "middle" };
                worksheet.getCell("A2").font = { italic: true };
        
                worksheet.addRow([`Time mode: ${timeMode}`]);
                worksheet.mergeCells(`A3:${lastColumnLetter}3`);
                worksheet.getCell("A3").alignment = { horizontal: "center", vertical: "middle" };
                worksheet.getCell("A3").font = { italic: true };
        
                worksheet.addRow([]); // Insert Empty Row Before Headers
        
                // 🔹 **Manually Add Column Headers**
                const headerRow = worksheet.addRow(allColumns);
                headerRow.eachCell((cell) => {
                    cell.font = { bold: true };
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "FFFFCC" },
                    };
                    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
                });
        
                // 🔹 Transform Data Dynamically
                const transformedInvoices = data.map((invoice, index) => {
                    const crewMembers = invoice.crewMembers || [];
                    return [
                        index + 1,
                        invoice.crewCode,
                        invoice.crewName,
                        timeMode === "UTC" ? format(invoice.flightDate, "yyyy-MM-dd") : format(invoice.flightDateLocalBase, "yyyy-MM-dd"),
                        invoice.flightNo,
                        invoice.crewRoute,
                        invoice.selectedFlights.length,
                        invoice.sectorType,
                        crewMembers.map(cm => cm.crewCode || "").join("\n"),
                        crewMembers.map(cm => cm.crewName || "").join("\n"),
                        crewMembers.map(cm => cm.post || "").join("\n"),
                        invoice.crewCount,
                        invoice.totalAmount || 0,
                        invoice.preorderCommission || 0,
                        invoice.directCommission || 0,
                        invoice.payableAmount || 0,
                        invoice.eachCrewPreorderCommission || 0,
                        invoice.eachCrewDirectCommission || 0,
                        ...categories.flatMap(category =>
                            allFields.map(field => invoice.categorySummary?.[category]?.[field] || 0)
                        ),
                    ];
                });
        
                // 🔹 Add Data Rows
                transformedInvoices.forEach(row => worksheet.addRow(row));
        
                // 🔹 Apply Styling
                worksheet.eachRow((row) => {
                    row.eachCell((cell) => {
                        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
                        cell.border = {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" },
                        };
                    });
                });
        
                // 🔹 Generate and Save File
                const buffer = await workbook.xlsx.writeBuffer();
                saveAs(new Blob([buffer]), fileName);
            } catch (error) {
                toast.error(error.message || "Failed to export to Excel!");
            }
        };
            
        if (!invoices || invoices.length === 0) {
            toast.error("No data available to export!");
            return;
        }
    
        // Add Timestamp to Filename
        const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, "").slice(0, 14);
        const fileName = `Invoices_${timestamp}.xlsx`;
    
        exportToExcel(invoices, fileName);
    };   

    // const handleCleanProducts = () => {    
    //     setConfirmDialogOpen(true);
    // };

    const executeCleanProducts = useCallback(async () => {
        try {
            if (!invoices || invoices.length === 0) {
                toast.error("No invoices available to delete.");
                return;
            }

            setLoading(true);

            const url = '/inflightsales/cleanproducts';
            const payload = { startDate, endDate };
            
            const { data } = await api.post(url, payload);              
            setInvoices(data.invoices);
            toast.success(`${data.modifiedCount} invoices from ${format(startDate, "dd/MM/yyyy")} to ${format(endDate, "dd/MM/yyyy")} cleaned successfully.`);
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message;
            toast.error(errorMessage || "Failed to clean invoices!");
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, invoices]);

    // const updateCommissionRate = useCallback(async () => {
    //     try {
    //         if (!invoices || invoices.length === 0) {
    //             toast.error("No invoices available to update.");
    //             return;
    //         }

    //         setLoading(true);

    //         const url = '/inflightsales/updatecommissionrate';
    //         const payload = { startDate, endDate };
            
    //         const { data } = await api.post(url, payload);              
    //         setInvoices(data.invoices);
    //         toast.success(`${data.modifiedCount} invoices from ${format(startDate, "dd/MM/yyyy")} to ${format(endDate, "dd/MM/yyyy")} updated successfully.`);
    //     } catch (err) {
    //         const errorMessage = err.response?.data?.message || err.message;
    //         toast.error(errorMessage || "Failed to update commission rate!");
    //     } finally {
    //         setLoading(false);
    //     }
    // }, [startDate, endDate, invoices]);

    const columns = useMemo(() => {
        const categoryInfo = invoices.reduce(
            (acc, invoice) => {
                const categorySummary = invoice?.categorySummary || {};
                Object.entries(categorySummary).forEach(([category, categoryData]) => {
                    acc.categories.add(category);
                    Object.keys(categoryData).forEach(field => acc.fields.add(field));
                });
                return acc;
            },
            { categories: new Set(), fields: new Set() }
        );
        
        // Convert sets to arrays
        const categories = Array.from(categoryInfo.categories)?.filter(category => category !== 'Equipment');
        const allFields = Array.from(categoryInfo.fields);
        
        const categoryColumns = categories.flatMap(category =>
            allFields.map(field => ({
                field: `${category.toLowerCase().replace(/&/g, "n")}${field}`,
                headerName: `${category}${capitalizeFirstLetter(field)}`,
                headerClassName: "theme-column--header",
                width: 150,
                align: "right",
                renderCell: (params) => {
                    const value = params?.row?.categorySummary?.[category]?.[field];
                    return <Box>{value ? value.toLocaleString() : ""}</Box>;
                },
            }))
        );

        return [
            {
                field: "no",
                headerName: "No",
                headerClassName: 'theme-column--header',
                width: 50,
                filterable: false,      
                sortable: false,
                renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
            },
            {
                field: "crewCode",
                headerName: "InputId",
                headerClassName: 'theme-column--header',
                width: 60,
            },
            {
                field: "crewName",
                headerName: "InputBy",
                headerClassName: 'theme-column--header',
                width: 280,
            },
            {
                field: "createdAt",
                headerName: "CreatedAt",
                headerClassName: 'theme-column--header', 
                width: 130, 
                valueFormatter: (value) => {
                    if (!value) return '';
                    return `${format(value, "HH:mm:ss dd/MM/yy")}`;
                },
            },
            {
                field: "updatedAt",
                headerName: "UpdatedAt",
                headerClassName: 'theme-column--header', 
                width: 130, 
                valueFormatter: (value) => {
                    if (!value) return '';
                    return `${format(value, "HH:mm:ss dd/MM/yy")}`;
                },
            },
            {
                field: timeMode === 'UTC' ? "flightDate" : "flightDateLocalBase",
                headerName: "RouteDate",
                headerClassName: 'theme-column--header',
                width: 100, 
                valueFormatter: (value) => {
                    if (!value) return '';
                    return `${format(value, "dd/MM/yy")}`;
                },
            },            
            {
                field: "flightNo",
                headerName: "FlightNo",
                width: 150,
                headerClassName: 'theme-column--header',
                cellClassName: "name-column--cell",
            },        
            {
                field: "crewRoute",
                headerName: "CrewRoute",
                headerClassName: 'theme-column--header',
                width: 165,
            },   
            {
                field: "sectorType",
                headerName: "SectorType",
                headerClassName: 'theme-column--header',
                width: 100,
            },      
            {
                field: 'selectedFlights',
                headerName: 'FlightList',
                width: isMobile ? 330 : 320,   
                renderCell: (params) => {
                    const sortedFlights = [...(params.row.selectedFlights || [])].sort((a, b) => {
                        const dateComparison = new Date(a.flightDate) - new Date(b.flightDate);
                        if (dateComparison !== 0) return dateComparison; 
                        return a.etd.localeCompare(b.etd); 
                    });                
                        
                    return (
                        <Box
                            sx={{
                                height: '100%',
                                alignItems: 'flex-start',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                            }}
                        >
                            {sortedFlights.map((flight, index) => (
                                <Box
                                    key={index}
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'right',
                                            whiteSpace: 'nowrap',
                                            width: 32,
                                        }}
                                    >
                                        {flight.flightNo}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'right',
                                            width: '54px',
                                        }}
                                    >
                                        {flight.flightReg || flight.acType}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'center',
                                            width: 45,
                                        }}
                                    >                                    
                                        {timeMode === 'UTC' ? format(flight.flightDate, "ddMMMyy") : format(flight.flightDateLocalBase, "ddMMMyy")}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'center',
                                            width: 30,
                                        }}
                                    >
                                        {timeMode === 'UTC' ? flight.etd : flight.etdLocalBase}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'center',                                        
                                            width: 25,
                                        }}
                                    >
                                        {flight.dep}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'center',                                        
                                            width: 25,
                                        }}
                                    >
                                        {flight.arr}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'center',
                                            width: 30,
                                        }}
                                    >
                                        {timeMode === 'UTC' ? flight.eta : flight.etaLocalBase}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    );
                },
            },
            {
                field: 'crewMembers',
                headerName: 'CrewMembers',
                width: isMobile ? 330 : 350,
                renderCell: (params) => {
                    const sortedCrewMembers = params.row.crewMembers
                        ?.map(member => {
                            const rank = crewRanks.find(rank => rank.rankCode === member.post) || {};
                            return { ...member, rankPriority: rank.rankPriority || Infinity };
                        })
                        .sort((a, b) => a.rankPriority - b.rankPriority);
                        
                    return (
                        <Box
                            sx={{
                                height: '100%',
                                alignItems: 'flex-start',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                            }}
                        >
                            {sortedCrewMembers?.map((member, index) => (
                                <Box
                                    key={index}
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'right',
                                            whiteSpace: 'nowrap',
                                            width: 30,
                                        }}
                                    >
                                        {member.crewCode}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'left',
                                        }}
                                    >
                                        {member.crewName}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'center',
                                        }}
                                    >
                                        ({member.post} | {member.base})
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    );
                },
            },
            {
                field: 'preorderProducts',
                headerName: 'PreorderProducts',
                width: isMobile ? 320 : 380,
                renderCell: (params) => {
                    const filteredProducts = params.row.products
                        ?.filter(product => product.preorderQuantity > 0);
                        
                    return (
                        <Box
                            sx={{
                                height: '100%',
                                alignItems: 'flex-start',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                            }}
                        >
                            {filteredProducts?.map((product, index) => {
                                const totalQuantity = product.preorderQuantity || 0;                            
                                return (
                                    <Box
                                        key={index}
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                textAlign: 'right',
                                                whiteSpace: 'nowrap',
                                                width: 12,
                                            }}
                                        >
                                            {`${index + 1}. `}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                textAlign: 'left',
                                                whiteSpace: 'nowrap',
                                                width: isMobile ? 220 : 280,
                                            }}
                                        >
                                            {product.productName}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                textAlign: 'right',
                                                whiteSpace: 'nowrap',
                                                width: 20,
                                            }}
                                        >
                                            {totalQuantity}
                                        </Typography>
                                    </Box>
                                )}
                            )}
                        </Box>
                    );
                },
            }, 
            {
                field: 'directProducts',
                headerName: 'DirectProducts',
                width: isMobile ? 320 : 380,
                renderCell: (params) => {
                    const filteredProducts = params.row.products
                        ?.filter(product => product.directQuantity > 0);
                        
                    return (
                        <Box
                            sx={{
                                height: '100%',
                                alignItems: 'flex-start',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                            }}
                        >
                            {filteredProducts?.map((product, index) => {
                                const totalQuantity = product.directQuantity || 0;                            
                                return (
                                    <Box
                                        key={index}
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                textAlign: 'right',
                                                whiteSpace: 'nowrap',
                                                width: 12,
                                            }}
                                        >
                                            {`${index + 1}. `}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                textAlign: 'left',
                                                whiteSpace: 'nowrap',
                                                width: isMobile ? 220 : 280,
                                            }}
                                        >
                                            {product.productName}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                textAlign: 'right',
                                                whiteSpace: 'nowrap',
                                                width: 20,
                                            }}
                                        >
                                            {totalQuantity}
                                        </Typography>
                                    </Box>
                                )}
                            )}
                        </Box>
                    );
                },
            },              
            ...categoryColumns,            
            {
                field: "totalAmount",
                headerName: "TotalAmount",
                headerClassName: 'theme-column--header',
                width: 150,
                align: 'right',
                valueFormatter: (params) => {
                    return params ? params.toLocaleString() : '';
                },
            },
            {
                field: "preorderAmount",
                headerName: "PreorderAmount",
                headerClassName: 'theme-column--header',
                width: 150,
                align: 'right',
                valueFormatter: (params) => {
                    return params ? params.toLocaleString() : '';
                },
            },
            {
                field: "directAmount",
                headerName: "DirectAmount",
                headerClassName: 'theme-column--header',
                width: 150,
                align: 'right',
                valueFormatter: (params) => {
                    return params ? params.toLocaleString() : '';
                },
            },        
            {
                field: "totalCommission",
                headerName: "TotalCommission",
                headerClassName: 'theme-column--header',
                width: 150,
                align: 'right',
                valueFormatter: (params) => {
                    return params ? params.toLocaleString() : '';
                },
            },
            {
                field: "preorderCommission",
                headerName: "PreorderCommission",
                headerClassName: 'theme-column--header',
                width: 150,
                align: 'right',
                valueFormatter: (params) => {
                    return params ? params.toLocaleString() : '';
                },
            },
            {
                field: "directCommission",
                headerName: "DirectCommission",
                headerClassName: 'theme-column--header',
                width: 150,
                align: 'right',
                valueFormatter: (params) => {
                    return params ? params.toLocaleString() : '';
                },
            },        
            {
                field: "payableAmount",
                headerName: "PayableAmount",
                headerClassName: 'theme-column--header',
                width: 150,
                align: 'right',
                valueFormatter: (params) => {
                    return params ? params.toLocaleString() : '';
                },
            },    
            {
                field: "crewCount",
                headerName: "CrewCount",
                headerClassName: 'theme-column--header',
                width: 100,
                align: 'right',
                valueFormatter: (params) => {
                    return params ? params.toLocaleString() : '';
                },
            },
            {
                field: "eachCrewTotalCommission",
                headerName: "Commission1Crew",
                headerClassName: 'theme-column--header',
                width: 150,
                align: 'right',
                valueFormatter: (params) => {
                    if (!params) return ''; 
                    const roundedValue = Math.ceil(params); 
                    return roundedValue.toLocaleString(); 
                },
            },
            {
                field: 'preorderComparison',
                headerName: 'Preorder Sell vs Consume',
                width: isMobile ? 300 : 350,
                renderCell: (params) => {
                    const sellProducts = params.row.products.filter(p => p.isInventory !== false) || [];
                    const consumeProducts = params.row.inventoryProducts || [];
            
                    // Create a unique list of all product names
                    const productNames = Array.from(new Set([
                        ...sellProducts.map(p => p.productName),
                        ...consumeProducts.map(p => p.productName)
                    ]));
            
                    // Compare preorderQuantity and preorderConsume for each product
                    const comparison = productNames.map(productName => {
                        const sellProduct = sellProducts.find(p => p.productName === productName) || {};
                        const consumeProduct = consumeProducts.find(p => p.productName === productName) || {};
                        const sellQty = sellProduct.preorderQuantity || 0;
                        const consumeQty = consumeProduct.preorderConsume || 0;
                        const difference = sellQty - consumeQty;
                        return { productName, difference };
                    }).filter(item => item.difference !== 0); // Show only differences
            
                    return (
                        <Box sx={{ height: '100%', alignItems: 'flex-start', overflowY: 'auto', overflowX: 'hidden' }}>
                            {comparison.length > 0 ? (
                                comparison.map((item, index) => (
                                    <Box key={index} display="flex" alignItems="center" gap={1}>
                                        <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'nowrap', width: 12 }}>
                                            {`${index + 1}.`}
                                        </Typography>
                                        <Typography variant="body2" sx={{ textAlign: 'left', whiteSpace: 'nowrap', width: isMobile ? 200 : 250 }}>
                                            {item.productName}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                textAlign: 'right',
                                                whiteSpace: 'nowrap',
                                                width: 20,
                                                color: item.difference > 0 ? 'green' : 'red',
                                            }}
                                        >
                                            {item.difference > 0 ? `+${item.difference}` : item.difference}
                                        </Typography>
                                    </Box>
                                ))
                            ) : (
                                <Typography variant="body2" sx={{ textAlign: 'center', color: 'gray' }}> </Typography>
                            )}
                        </Box>
                    );
                },
            },            
            {
                field: 'directComparison',
                headerName: 'Direct Sell vs Consume',
                width: isMobile ? 300 : 350,
                renderCell: (params) => {
                    const sellProducts = params.row.products.filter(p => p.isInventory !== false) || [];
                    const consumeProducts = params.row.inventoryProducts || [];
            
                    // Create a unique list of all product names
                    const productNames = Array.from(new Set([
                        ...sellProducts.map(p => p.productName),
                        ...consumeProducts.map(p => p.productName)
                    ]));
            
                    // Compare directQuantity and directConsume for each product
                    const comparison = productNames.map(productName => {
                        const sellProduct = sellProducts.find(p => p.productName === productName) || {};
                        const consumeProduct = consumeProducts.find(p => p.productName === productName) || {};
                        const sellQty = sellProduct.directQuantity || 0;
                        const consumeQty = consumeProduct.directConsume || 0;
                        const difference = sellQty - consumeQty;
                        return { productName, difference };
                    }).filter(item => item.difference !== 0); // Show only differences
            
                    return (
                        <Box sx={{ height: '100%', alignItems: 'flex-start', overflowY: 'auto', overflowX: 'hidden' }}>
                            {comparison.length > 0 ? (
                                comparison.map((item, index) => (
                                    <Box key={index} display="flex" alignItems="center" gap={1}>
                                        <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'nowrap', width: 12 }}>
                                            {`${index + 1}.`}
                                        </Typography>
                                        <Typography variant="body2" sx={{ textAlign: 'left', whiteSpace: 'nowrap', width: isMobile ? 200 : 250 }}>
                                            {item.productName}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                textAlign: 'right',
                                                whiteSpace: 'nowrap',
                                                width: 20,
                                                color: item.difference > 0 ? 'green' : 'red',
                                            }}
                                        >
                                            {item.difference > 0 ? `+${item.difference}` : item.difference}
                                        </Typography>
                                    </Box>
                                ))
                            ) : (
                                <Typography variant="body2" sx={{ textAlign: 'center', color: 'gray' }}> </Typography>
                            )}
                        </Box>
                    );
                },
            },
        ]
    }, [timeMode, isMobile, crewRanks, invoices]);
    
    const nonMobileHiddenColumns = {
        _id: false,      
        // createdAt: false,
        updatedAt: false, 
        totalAmount: false,
        preorderAmount: false,
        directAmount: false,
        totalCommission: false,  
        preorderCommission: false,  
        directCommission: false, 
        // preorderComparison: false,
        // directComparison: false,
    };
    
    const actionColumn = useMemo(() => {        
        const activeCount = [canEditInvoice, canEditCrewList].filter(Boolean).length;
        if (activeCount === 0) return [];
        const baseWidth = 105 - (2 - activeCount) * 20;
        
        return [{
            field: "action",
            headerName: "Action",
            width: baseWidth,
            filterable: false,      
            sortable: false,
            renderCell: (params) => (
                <FlexCenter
                    gap={1}
                    sx={{ width: '100%', height: '100%' }}
                >            
                    {canEditInvoice && (
                        <Tooltip title="View">
                            <IconButton
                                aria-label="View"
                                sx={{ color: "primary.dark" }}
                                onClick={() => {
                                    if (!canEditInvoice) return;

                                    setSelectedInvoiceId(params.row._id);
                                    setSelectedCrewCode(params.row.crewCode);
                                    setSelectedCrewName(params.row.crewName);
                                    setSelectedFlights(params.row.selectedFlights);
                                    setCrewMembers(params.row.crewMembers);
                                    const latestProducts = params.row.products.map((product) => ({
                                        ...product,
                                        price: product.price, 
                                    }));
                                    setSelectedProducts(latestProducts);
                                    handleViewClick()}
                                }
                            >
                                <Visibility />
                            </IconButton>                
                        </Tooltip>
                    )}
                    {canEditCrewList && (
                        <Tooltip title="Edit crew list">
                            <IconButton
                                aria-label="Edit"
                                sx={{ color: "primary.dark" }}
                                onClick={() => {      
                                    if (!canEditCrewList) return;

                                    const sortedCrewMembers = params.row.crewMembers
                                        ?.map(member => {
                                            const rank = crewRanks.find(rank => rank.rankCode === member.post) || {};
                                            return { ...member, rankPriority: rank.rankPriority || Infinity };
                                        })
                                        ?.sort((a, b) => a.rankPriority - b.rankPriority); 

                                    setSelectedInvoiceId(params.row._id);      
                                    setSelectedFlights(params.row.selectedFlights);
                                    setCrewMembers(sortedCrewMembers);     
                                    handleEditClick(params.row._id)}
                                }
                            >
                                <PersonPin />
                            </IconButton>
                        </Tooltip>
                    )}                        
                </FlexCenter>
            ),
        }]
    }, [
        canEditInvoice,
        canEditCrewList,
        crewRanks,
    ]);

    const columnsToShow = [...columns, ...actionColumn];

        
    return (
        <Box className="main-container-box">             
            <FlexBetween>
                <Header 
                    title="Invoices" 
                    subtitle={`${loading ? 'Loading...' : 'List of Invoices'}`}
                /> 
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>                        
                        {/* <Tooltip title="Clean products with zero quantity" arrow>
                            <span>
                                <StyledButton
                                    sx={{ display: "none" }}
                                    onClick={handleCleanProducts}
                                >
                                    <CleaningServices />
                                </StyledButton>
                            </span>
                        </Tooltip> */}
                        {/* <Tooltip title="Update commission rate" arrow>
                            <span>
                                <StyledButton
                                    sx={{ display: "none" }}
                                    onClick={updateCommissionRate}
                                >
                                    <Update />
                                </StyledButton>
                            </span>
                        </Tooltip>   */}
                        <Tooltip title="Download Excel file" arrow>
                            <span>
                                <StyledButton 
                                    onClick={() => {
                                        if (!canExport) return;
                                        handleExcelJSDownload();
                                        logClientAction("EXPORT_REPORT", { fileName: "Invoices", format: "Excel", startDate, endDate });
                                    }} 
                                >
                                    <Download />
                                </StyledButton>
                            </span>
                        </Tooltip>
                        <FilterButton
                            filters={filters}
                            defaultFilters={defaultFilters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}                
            </FlexBetween>              
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </FlexCenter>
            ) : (
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    gap={2}
                >
                    <Box
                        height={LAYOUT_HEIGHT}
                        sx={{ gridColumn: "span 12" }}
                    >
                        {isMobile ? (
                            <CardList
                                rows={filteredInvoices}
                                primaryField="crewName"
                                secondaryField="crewCode"
                                isActiveField={null}
                                minWidth={98}
                                fields={[
                                    { label: "Route Date",          key: "flightDate",                  render: (r) => r.flightDate ? format(new Date(r.flightDate), "dd/MM/yy") : null },
                                    { label: "Flight No",           key: "flightNo" },
                                    { label: "Crew Route",          key: "crewRoute" },
                                    { label: "Sector Type",         key: "sectorType" },
                                    { label: "Crew Count",          key: "crewCount" },
                                    { label: "Pre Amount",          key: "preorderAmount",     cols: 2, render: (r) => formatNumber(r.preorderAmount) },
                                    { label: "Dir Amount",          key: "directAmount",       cols: 2, render: (r) => formatNumber(r.directAmount) },
                                    { label: "Total Amount",        key: "totalAmount",                 render: (r) => formatNumber(r.totalAmount) },
                                    { label: "Pre Commission",      key: "preorderCommission", cols: 2, render: (r) => formatNumber(r.preorderCommission) },
                                    { label: "Dir Commission",      key: "directCommission",   cols: 2, render: (r) => formatNumber(r.directCommission) },
                                    { label: "Total Commission",    key: "totalCommission",    cols: 2, render: (r) => formatNumber(r.totalCommission) },
                                    { label: "Payable Amount",      key: "payableAmount",      cols: 2, render: (r) => formatNumber(r.payableAmount) },
                                ]}
                                canView={canEditInvoice}
                                canEdit={false}
                                canDelete={false}
                                onView={(id) => {
                                    const item = filteredInvoices.find(inv => String(inv._id) === String(id));
                                    if (item) {
                                        setSelectedInvoiceId(item._id);
                                        setSelectedCrewCode(item.crewCode);
                                        setSelectedCrewName(item.crewName);
                                        setSelectedFlights(item.selectedFlights);
                                        setCrewMembers(item.crewMembers);
                                        setSelectedProducts(item.products.map(p => ({ ...p, price: p.price })));
                                        handleViewClick();
                                    }
                                }}
                            />
                        ) : (
                            <DataGridComponent
                                rows={filteredInvoices}
                                columns={columnsToShow}
                                loading={loading}
                                props={{
                                    getRowClassName: (params) =>
                                        params.row.isInventoryUpdated === false ? "inventory-not-updated" : "",
                                    initialState: {
                                        pagination: { paginationModel: { pageSize: 100 } },
                                        columns: { columnVisibilityModel: nonMobileHiddenColumns },
                                    },
                                }}
                                sx={{
                                    "& .MuiDataGrid-row.inventory-not-updated": {
                                        backgroundColor: `${theme.palette.green.light} !important`,  
                                    },
                                }}
                            />
                        )}
                    </Box>                
                </Box>
            )}

            <ConfirmDialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={executeCleanProducts}
            />

            {isFilterDialogOpen &&
                <InvoiceFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    isInventoryUpdatedVisible={true}
                    currentFilters={filters}
                />
            }

            {openInvoiceDialog && 
                <InvoiceDialog
                    open={openInvoiceDialog}
                    onClose={handleCloseInvoiceDialog}
                    selectedInvoiceId={selectedInvoiceId} 
                    crewCode={selectedCrewCode} 
                    crewName={selectedCrewName}
                    selectedFlights={selectedFlights}
                    crewMembers={crewMembers}
                    selectedProducts={selectedProducts}
                    onInvoiceSaved={handleInvoiceSaved}
                />
            }

            {openCrewListDialog &&
                <CrewListDialog
                    open={openCrewListDialog}
                    onClose={handleCloseCrewListDialog}
                    invoiceId={selectedInvoiceId}
                    selectedFlights={selectedFlights}
                    crewMembers={crewMembers}                        
                    onCrewListSaved={handleCrewListSaved}
                />  
            }
        </Box>
    );
};

export default Invoices;
